import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  addQueryWithReplace,
  removeQueryWithReplace,
  QueryVal,
  QueryValUrl,
  getLinkQueryParamUrl,
} from 'Helpers/Query';
import {
  getStartOfDayUTCFromLocal,
  getStartOfDayLocalFromUTC,
} from 'Helpers/DateTime';
import TextField from 'Components/UI/TextField';
import DateField from 'Components/UI/DateField';
import Button from 'Components/UI/Button';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import moment from 'moment';
import {
  NOT_IN,
  EQUALS,
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  IN,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  LIKE,
  NOT_EQUAL,
} from 'Consts/queryParams';
import Checkbox from 'Components/UI/Checkbox';
import HelperText from 'Components/UI/Accounts/AccountApplication/HelperText';
import Autocomplete from '../../Autocomplete';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  filterContainer: {},
  filterSelectContainer: {
    width: theme.dimensions.sm,
  },
  filterInputContainer: {
    width: theme.dimensions.sm,
  },
  filterTextContainer: {},
  filterActions: {
    display: 'flex',
    justifyContent: 'space-between',
    '& button': {
      flexGrow: 1,
      margin: theme.spacing(1),
    },
  },
  filterAction: {
  },
  filterDateContainer: {
    width: '100%',
  },
  filterParamOperatorsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  helperText: {
    marginBottom: '0.5rem',
  },
});

function MetaQueryForm({
  className,
  classes,
  collection,
  linkQueryParamName,
  onSubmit,
  defaultFilters,
  availableFilters: availableFiltersProp,
  getAvailableFilters,
  AdditionalActions,
  onReset,
  ...rest
}) {
  const [filters, setFilters] = useState({ ...defaultFilters });
  const availableFilters = getAvailableFilters ? getAvailableFilters(collection) : availableFiltersProp;
  // This can probably be exported directly from the consts file
  const operatorPrefixes = [
    IN,
    NOT_IN,
    NOT_EQUAL,
    GREATER_THAN,
    GREATER_THAN_OR_EQUAL,
    LESS_THAN,
    LESS_THAN_OR_EQUAL,
    LIKE,
    EQUALS,
  ];

  // getFilterMeta returns the meta for a single query parameter.
  // filter is an object that must contain a query and a name
  // query represents the query name the meta belongs to (See the rel attribute on the resource)
  // name is the name of the query parameter
  const getFilterMeta = (filter) => {
    const filterQuery = collection.meta().query(filter.query);
    if (!filterQuery) {
      throw new Error(`Unknown filter query ${filter.query} in ${filter.name}. Make sure the rel is set in your resource queries definition`);
    }
    const filterMeta = filterQuery.field(filter.name);
    if (!filterMeta) {
      throw new Error(`Unknown filter ${filter.name}. Make sure it is defined in your resource queries definition`);
    }
    return filterMeta;
  };

  // stripOperators will remove query param operator such as like| from the query parameter value
  const stripOperators = (value) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const operatorPrefix of operatorPrefixes) {
      const prefix = `${operatorPrefix}|`;
      if (value.indexOf(prefix) === 0) {
        return value.slice(prefix.length);
      }
    }
    return value;
  };

  const getOperator = (value) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const operatorPrefix of operatorPrefixes) {
      const prefix = `${operatorPrefix}|`;
      if (value.indexOf(prefix) === 0) {
        return operatorPrefix;
      }
    }
    return null;
  };

  const splitQueryParamValue = (value) => {
    let values = value.split(',');
    // The value may not have had any commas, but it still needs to be an array
    if (!Array.isArray(values)) {
      values = [values];
    }
    return values;
  };

  const loadFilterParamValues = (linkUrl) => {
    const filterValues = {};
    availableFilters.forEach((filter) => {
      const filterMeta = getFilterMeta(filter);
      const paramKey = filter.name;
      let paramValue = QueryValUrl(linkUrl, paramKey);

      // If the link did not contain the filter, but the query parameter can appear in the main url, then check for it there
      if (!paramValue && filter._setQueryParam) {
        paramValue = QueryVal(paramKey);
      }

      // If the link query parameter has a filter query param that is different than what is currently in
      // the filter, then set it in the filter
      // if (paramValue && paramValue !== filters[paramValue]) {
      if (paramValue) {
        let val = paramValue;

        if (filterMeta.attr('type') === 'multipicklist') {
          val = splitQueryParamValue(val);
        }

        filterValues[paramKey] = {
          value: stripOperators(val),
        };
        const operator = getOperator(val);
        if (operator && operator !== EQUALS && operator !== IN) {
          filterValues[paramKey].operator = operator;
        }
      }
    });
    return filterValues;
  };

  useEffect(() => {
    if (QueryVal(linkQueryParamName)) {
      const linkUrl = getLinkQueryParamUrl(collection, linkQueryParamName);
      if (linkUrl) {
        const filterValues = loadFilterParamValues(linkUrl);
        const mergedFilters = { ...filters, ...filterValues };
        setFilters(mergedFilters);
      }
    }
  }, [linkQueryParamName]);

  const clearFilters = () => {
    setFilters({ ...defaultFilters });
    onReset();
  };

  // handleFilterSubmit will iterate through the list of available filters and check if
  // there are associated entries in the filter set. If the values are not set or are undefined,
  // the query parameters are removed from the url encoded in linkQueryParamName
  // If there are associated values, the query parameter is set in the url encoded in linkQueryParamName
  // If a query parameter has a _setQueryParam attribute, then the associated query parameter will ALSO be set in the url
  // outside of the linkQueryParamName url. The main usecase for this is to be able to reload the household context
  // by updating the householdId param
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const linkUrl = getLinkQueryParamUrl(collection, linkQueryParamName);

    availableFilters.forEach((availableFilter) => {
      const key = availableFilter.name;
      const value = filters[key]?.value;

      if (value && (!Array.isArray(value) || (value.length > 0))) {
        let operatorPrefix = '';
        if (filters[key]?.operator && filters[key]?.operator !== EQUALS && filters[key].operator !== IN) {
          operatorPrefix = `${filters[key].operator}|`;
        }

        if (availableFilter._setQueryParam) {
          addQueryWithReplace({ [key]: value });
        }

        linkUrl.searchParams.set(key, `${operatorPrefix}${value.toString()}`);
      } else {
        if (availableFilter._setQueryParam) {
          removeQueryWithReplace(key);
        }
        linkUrl.searchParams.delete(key);
      }
    });
    linkUrl.searchParams.set('offset', '0');
    onSubmit(linkUrl.toString());
  };
  if (!filters) {
    return <LoadingIndicator />;
  }

  return (
    <form>
      <Paper variant="outlined" className={classNames(classes.root, className)} {...rest}>
        <div className={classes.filterContainer}>
          {availableFilters.map((filter) => {
            const filterMeta = getFilterMeta(filter);
            let filterOperators = filterMeta.operators().list();
            if (filter.overrideOperators) {
              filterOperators = filterOperators.filter(v => filter.overrideOperators.includes(v.id()));
            }

            // We iterate over the filter operators, but we use the filter name as the key in the filters object
            // This will cause subsequent operators to overwrite the value of the previous operator
            if (filterOperators.length > 1) {
              throw new Error('Need to fix a bug that prevents multiple operator from being used together. See Kevin');
            }

            return (
              <div key={`${filterMeta.attr('name')}-container`} className={classes.filterParamOperatorsContainer}>
                {filterOperators.map((operator) => {
                  if (filterMeta.attr('type') === 'picklist'
                    || filterMeta.attr('type') === 'multipicklist'
                    // These two checks are here because some query parameters are incorrectly marked as non picklist
                    // types, but we need to be able to display a select control for them. One example is householdId on the
                    // actionItem resource
                    || filterMeta.attr('options')
                    || filter.overrideOptions
                  ) {
                    let defaultValue = '';
                    if (filterMeta.attr('type') === 'multipicklist') {
                      defaultValue = [];
                    }
                    const filterValue = filters[filter.name]?.value || defaultValue;
                    return (
                      <Autocomplete
                        key={`${filterMeta.attr('name')}-operator-${operator.id()}`}
                        classes={{
                          root: classes.filterSelectContainer,
                        }}
                        value={filterValue}
                        onChange={(evt) => {
                          const evtValue = evt.target.value;
                          setFilters((prevState) => {
                            return {
                              ...prevState,
                              [filter.name]: {
                                value: evtValue,
                                operator: operator.id(),
                              },
                            };
                          });
                        }}
                        options={filter.overrideOptions ? filter.overrideOptions : filterMeta
                          .options().list().map(option => ({
                            name: option.attr('prompt'),
                            value: option.attr('id'),
                          }))
                        }
                        label={filter.prompt || filterMeta.attr('prompt')}
                        SelectProps={{
                          multiple: filterMeta.attr('type') === 'multipicklist',
                        }}
                        test={`meta-query-form-autocomplete-${filterMeta.attr('name')}`}
                      />
                    );
                  }

                  if (filterMeta.attr('type') === 'string') {
                    return (
                      <TextField
                        classes={{
                          root: classes.filterTextContainer,
                          filterInput: classes.filterInputContainer,
                        }}
                        key={`${filterMeta.attr('name')}-operator-${operator.id()}`}
                        label={filter.prompt || filterMeta.attr('prompt')}
                        variant="outlined"
                        fullWidth
                        onChange={(evt) => {
                          let evtValue = evt.target.value;
                          setFilters((prevState) => {
                            if (!evtValue) {
                              evtValue = undefined;
                            }
                            return {
                              ...prevState,
                              [filter.name]: {
                                value: evtValue,
                                operator: operator.id(),
                              },
                            };
                          });
                        }}
                        value={filters[filter.name]?.value || ''}
                      />
                    );
                  }

                  if (filterMeta.attr('type') === 'date' || filterMeta.attr('type') === 'datetime') {
                    const dateValue = filters[filter.name]?.value ? getStartOfDayLocalFromUTC(filters[filter.name]?.value) : '';

                    return (
                      <DateField
                        classes={{
                          root: classes.filterDateContainer,
                        }}
                        key={`${filterMeta.attr('name')}-operator-${operator.id()}`}
                        label={filter.prompt || filterMeta.attr('prompt')}
                        openTo="year"
                        variant="outlined"
                        onChange={(dateIsoString) => {
                          // This function will take a date value from the date picker and will convert it to
                          // the start of day UTC of the same day the date was chosen in the client.
                          // IE - if you're on the east coast and you select August 16th 2021, the date
                          // picker is going to select 2021-08-16T00:00:00-04:00
                          // When we send up to the server we need to send the start of day UTC, so
                          // getStartOfDayUTCFromLocal will convert the start of day local to UTC, which will
                          // result in an ISO time of 2021-08-16T00:00:00Z.
                          let evtValue;
                          if (dateIsoString) {
                            const startOfDayUTC = getStartOfDayUTCFromLocal(dateIsoString);
                            if (operator.id() === LESS_THAN_OR_EQUAL || operator.id() === LESS_THAN) {
                              evtValue = moment.utc(startOfDayUTC).endOf('day').format();
                            } else {
                              evtValue = startOfDayUTC;
                            }
                          }
                          setFilters((prevState) => {
                            return {
                              ...prevState,
                              [filter.name]: {
                                value: evtValue,
                                operator: operator.id(),
                              },
                            };
                          });
                        }}
                        value={dateValue}
                      />
                    );
                  }

                  if (filterMeta.attr('type') === 'bool') {
                    return (
                      <Checkbox
                        classes={{
                          root: classes.filterTextContainer,
                          filterInput: classes.filterInputContainer,
                        }}
                        key={`${filterMeta.attr('name')}-operator-${operator.id()}`}
                        label={filter.prompt || filterMeta.attr('prompt')}
                        variant="outlined"
                        fullWidth
                        onChange={(e) => {
                          setFilters((prevState) => {
                            return {
                              ...prevState,
                              [filter.name]: {
                                value: e?.target?.checked,
                                operator: operator.id(),
                              },
                            };
                          });
                        }}
                        value={Boolean(filters[filter.name]?.value) || false}
                      />
                    );
                  }

                  return null;
                })}
                {filter.helperText && <HelperText className={classes.helperText} text={filter.helperText} />}
              </div>
            );
          })}
        </div>
        <div className={classes.filterActions}>
          <Button className={classes.filterAction} onClick={clearFilters} color="default" variant="contained">Reset Filters</Button>
          <Button className={classes.filterAction} onClick={handleFilterSubmit} type="submit" color="primary" variant="contained" test="meta-query-form-apply-filters">Apply Filters</Button>
        </div>
        <div className={classes.filterActions}>
          {AdditionalActions}
        </div>
      </Paper>
    </form>
  );
}

MetaQueryForm.defaultProps = {
  className: '',
  linkQueryParamName: 'link',
  defaultFilters: {},
  getAvailableFilters: null,
  AdditionalActions: null,
  onReset: () => {},
};

MetaQueryForm.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  collection: PropTypes.object.isRequired,
  linkQueryParamName: PropTypes.string,
  availableFilters: PropTypes.array.isRequired,
  getAvailableFilters: PropTypes.func,
  defaultFilters: PropTypes.object,
  AdditionalActions: PropTypes.any,
  onReset: PropTypes.func,
};

export default withStyles(styles)(MetaQueryForm);
